import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Form, ListGroup } from "react-bootstrap";
import Spinner from "../../../components/Spinner";
import SubmitButton from "../../../components/SubmitButton";
import {
  HouseholdPerson,
  SlantPerson,
  SwitchDecision,
  stepErrorMessage,
  useSlantPeople,
} from "./api";

/**
 * A per-person review row assembled CLIENT-SIDE: one AC contact, the advisor's
 * chosen Slant person id (seeded from the server's `auto` suggestion), and
 * whether the server auto-proposed that link.
 */
export type ReviewRow = {
  contactId: number;
  name: string;
  email: string | null;
  /** True when the server auto-proposed this link (pre-filled). */
  autoLinked: boolean;
};

/**
 * Step 3 — Match people.
 *
 * Reads `slant-people` for the chosen Slant household: the Slant people (the
 * dropdown options) plus the server's proposed links. Auto-proposed rows are
 * pre-filled; the advisor confirms or overrides each. Advancing is BLOCKED until
 * every person carries a decision. Confirming hands the resolved decisions (and
 * display rows) UP to the wizard — nothing is written here.
 */
const SwitchToSlantStep3 = ({
  householdId,
  slantHouseholdId,
  people: householdPeople,
  onConfirmed,
}: {
  householdId: number;
  slantHouseholdId: string;
  people: HouseholdPerson[];
  /** Hands the resolved decisions + display rows + Slant people up to Step 4. */
  onConfirmed: (
    decisions: SwitchDecision[],
    rows: ReviewRow[],
    slantPeople: SlantPerson[],
  ) => void;
}) => {
  const slant = useSlantPeople(householdId, slantHouseholdId);

  const slantPeople: SlantPerson[] = useMemo(
    () => slant.data?.data.people ?? [],
    [slant.data],
  );

  // The server's proposed contact→slant links, indexed by AC contact id.
  const suggestionByPerson = useMemo(() => {
    const map = new Map<number, { slantPersonId: string; auto: boolean }>();
    for (const suggestion of slant.data?.data.suggestions ?? []) {
      map.set(suggestion.contact_id, {
        slantPersonId: suggestion.slant_person_id,
        auto: suggestion.auto,
      });
    }
    return map;
  }, [slant.data]);

  const rows: ReviewRow[] = useMemo(
    () =>
      householdPeople.map((person) => ({
        contactId: person.contact_id,
        name: person.name,
        email: person.email ?? null,
        autoLinked: suggestionByPerson.get(person.contact_id)?.auto ?? false,
      })),
    [householdPeople, suggestionByPerson],
  );

  // The advisor's per-person choice: the chosen Slant person id (or "" when
  // unmatched). Seeded from the server's suggestions once the read lands.
  const [choices, setChoices] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!slant.isSuccess) {
      return;
    }
    setChoices((current) => {
      if (Object.keys(current).length > 0) {
        return current;
      }
      const seeded: Record<number, string> = {};
      for (const person of householdPeople) {
        seeded[person.contact_id] =
          suggestionByPerson.get(person.contact_id)?.slantPersonId ?? "";
      }
      return seeded;
    });
  }, [slant.isSuccess, householdPeople, suggestionByPerson]);

  const allMatched =
    rows.length > 0 &&
    rows.every((row) => (choices[row.contactId] ?? "") !== "");

  const onSubmit = () => {
    const decisions: SwitchDecision[] = rows.map((row) => ({
      contact_id: row.contactId,
      slant_person_id: choices[row.contactId],
    }));
    onConfirmed(decisions, rows, slantPeople);
  };

  if (slant.isPending) {
    return (
      <div className="d-flex align-items-center">
        <Spinner as="span" size="sm" className="me-2" />
        <span>Matching people to Slant…</span>
      </div>
    );
  }

  if (slant.isError) {
    return (
      <Alert variant="danger">
        {stepErrorMessage(slant.error, "Failed to load Slant people to match.")}
      </Alert>
    );
  }

  return (
    <div>
      <h5>Match people to Slant</h5>
      <p className="text-muted">
        AdviceCloud pre-filled the people it could auto-match and listed the
        Slant contacts for the rest. Confirm or override each match — you
        can&apos;t continue until everyone is matched.
      </p>

      {rows.length === 0 ? (
        <Alert variant="warning">No people to match on this household.</Alert>
      ) : (
        <ListGroup className="mb-3">
          {rows.map((row) => {
            const chosen = choices[row.contactId] ?? "";
            const isUnmatched = chosen === "";
            return (
              <ListGroup.Item key={row.contactId}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>
                    {row.name}
                    {row.email ? (
                      <small className="text-muted"> · {row.email}</small>
                    ) : null}
                  </span>
                  {row.autoLinked && !isUnmatched ? (
                    <Badge bg="success" pill>
                      Auto-linked
                    </Badge>
                  ) : isUnmatched ? (
                    <Badge bg="warning" pill>
                      Unmatched
                    </Badge>
                  ) : (
                    <Badge bg="secondary" pill>
                      Matched
                    </Badge>
                  )}
                </div>
                <Form.Select
                  aria-label={`Slant contact for ${row.name}`}
                  value={chosen}
                  onChange={(event) => {
                    const { value } = event.target;
                    setChoices((current) => ({
                      ...current,
                      [row.contactId]: value,
                    }));
                  }}
                >
                  <option value="">No match — select a Slant contact…</option>
                  {slantPeople.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.name}
                      {person.email ? ` · ${person.email}` : ""}
                    </option>
                  ))}
                </Form.Select>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}

      <div className="d-flex justify-content-end">
        <SubmitButton
          label="Confirm matches — review & switch"
          onClick={onSubmit}
          isSubmitting={false}
          disabled={!allMatched}
        />
      </div>
    </div>
  );
};

export default SwitchToSlantStep3;
